import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { safeDivide } from 'ts-utils';
import { Color } from '../../config/Color';
import { appOverride } from '../../util/AppUtils';
import { TickColorable } from '../assets/icons/TickColorable';
import { BodyXS } from './Typography';

export type LoadingBarProps = {
  title: string;
  icon?: string;
  /** Starting progress value */
  startNumber: number;
  /** Ending progress value */
  endNumber: number;
  /** Delay before animation starts in milliseconds. Defaults to 0. */
  delayMs?: number;
  /** Duration of the animation in milliseconds */
  durationMs: number;
  /** If true, bar will never animate regardless of other props */
  isStatic?: boolean;
  onComplete?: () => void;
};

export const LoadingBar: React.FC<LoadingBarProps> = ({
  title,
  icon,
  startNumber,
  endNumber,
  delayMs = 0,
  durationMs,
  isStatic = false,
  onComplete,
}) => {
  const [currentProgress, setCurrentProgress] = useState(startNumber);

  useEffect(
    function animateProgress() {
      // Don't animate if explicitly marked as static
      if (isStatic) {
        return;
      }

      const timer = setTimeout(() => {
        // Animate progress with stuttering effect
        let startTime: number;
        let lastProgress = startNumber;

        const animate = (timestamp: number) => {
          if (startTime === undefined) startTime = timestamp;
          const elapsed = timestamp - startTime;
          const baseProgress = Math.min(safeDivide(elapsed, durationMs), 1);

          // Add stuttering effect - sometimes faster, sometimes slower
          const stutterFactor = 0.8 + Math.random() * 0.4; // Random between 0.8 and 1.2
          let newProgress = startNumber + (endNumber - startNumber) * baseProgress * stutterFactor;

          // Ensure progress doesn't go backwards and doesn't exceed target or 100
          newProgress = Math.max(lastProgress, Math.min(newProgress, Math.min(endNumber, 100)));

          // Add small random variations for realistic stuttering
          if (baseProgress < 0.95) {
            // Don't add randomness near the end
            const variation = (Math.random() - 0.5) * 2; // Random between -2 and +2
            newProgress = Math.max(lastProgress, Math.min(newProgress + variation, 100));
          }

          lastProgress = newProgress;
          setCurrentProgress(Math.round(newProgress));

          if (baseProgress < 1) {
            // Add random delay between frames for stuttering effect
            const nextDelay = 50 + Math.random() * 100; // 50-150ms delay
            setTimeout(() => requestAnimationFrame(animate), nextDelay);
          } else {
            // Ensure we always reach the target at the end
            setCurrentProgress(Math.min(endNumber, 100));
          }
        };

        requestAnimationFrame(animate);
      }, delayMs);

      return () => clearTimeout(timer);
    },
    [startNumber, endNumber, delayMs, durationMs]
  );

  const isComplete = currentProgress >= Math.min(endNumber, 100);
  const showTick = currentProgress >= 100;

  useEffect(
    function callOnCompleteWhenFinished() {
      if (isComplete && onComplete !== undefined) {
        onComplete();
      }
    },
    [isComplete, onComplete]
  );

  const renderStatusIcon = () => {
    if (showTick) {
      return <TickColorable color={Color.action.default} size={16} />;
    }
    if (isComplete && !showTick) {
      return <BlankCircle />;
    }
    // For static bars, don't show spinner - show blank circle instead
    if (isStatic) {
      return <BlankCircle />;
    }
    return <SpinnerIcon />;
  };

  return (
    <LoadingBarContainer>
      <LoadingBarHeader>
        <TitleContainer>
          {icon !== undefined && (
            <TitleIconContainer>
              <img src={icon} alt="" style={{ width: '16px', height: '16px' }} />
            </TitleIconContainer>
          )}
          <BodyXS>{title}</BodyXS>
        </TitleContainer>
        <RightContainer>
          <ProgressText>{currentProgress}%</ProgressText>
          <IconContainer>{renderStatusIcon()}</IconContainer>
        </RightContainer>
      </LoadingBarHeader>
      <ProgressBarContainer>
        <ProgressBarFill width={currentProgress} />
      </ProgressBarContainer>
    </LoadingBarContainer>
  );
};

const LoadingBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const LoadingBarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const RightContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const IconContainer = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(2px);
`;

const spinnerRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerIcon = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${Color.decorative.muted};
  border-top: 2px solid ${Color.flow.accent};
  border-radius: 50%;
  animation: ${spinnerRotate} 1s linear infinite;
`;

const BlankCircle = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${Color.decorative.muted};
  border-radius: 50%;
  background: transparent;
`;

const ProgressText = styled(BodyXS)`
  color: ${appOverride({ nerva: Color.content.default, default: Color.content.muted })};
  font-weight: normal;
  min-width: 40px;
  text-align: right;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${Color.decorative.muted};
  border-radius: 2px;
  overflow: hidden;
  align-items: baseline;
`;

const ProgressBarFill = styled.div<{ width: number }>`
  height: 100%;
  width: ${({ width }) => width}%;
  background-color: ${Color.flow.accent};
  transition: width 0.3s ease;
  border-radius: 2px;
`;

const TitleIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1;
  transform: translateY(-8px);

  img {
    ${appOverride({
      nerva: 'filter: brightness(0) invert(1);',
      default: '',
    })}
  }
`;
